import React, { useCallback, useContext, useEffect, useState } from "react";

import { AppContext } from "../../AppContext";
import AuthService from "../../AuthService";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";

import TrendingDown from "@material-ui/icons/TrendingDown";

import dashboardStyle from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { warningCardHeader } from "assets/jss/material-dashboard-react.js";

/** Discrete priority: 0–9 green, 10–30 orange (warning CardIcon gradient), 31+ red. */
const PRIORITY_GREEN = "#5DBB63";
const PRIORITY_ORANGE_BACKGROUND = warningCardHeader.background;
const PRIORITY_RED = "#d62546";

function priorityBackgroundAndTextColor(p) {
  const clipped = Math.max(0, Math.min(100, p));
  let background;
  let color;
  if (clipped < 10) {
    background = PRIORITY_GREEN;
    color = "rgba(0, 0, 0, 0.88)";
  } else if (clipped <= 30) {
    background = PRIORITY_ORANGE_BACKGROUND;
    color = "rgba(0, 0, 0, 0.88)";
  } else {
    background = PRIORITY_RED;
    color = "#fff";
  }
  return { background, color };
}

function renderPriorityLabel(priority) {
  if (priority == null || priority === "") {
    return "";
  }
  const n = Number(priority);
  if (Number.isNaN(n)) {
    return "";
  }
  const p = Math.max(0, Math.min(100, n));
  const { background, color } = priorityBackgroundAndTextColor(p);
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: "40px",
        padding: "4px 12px",
        borderRadius: "16px",
        fontWeight: 600,
        fontSize: "13px",
        lineHeight: 1.25,
        textAlign: "center",
        background,
        color,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
      }}
    >
      {Math.round(n)}
    </span>
  );
}

const useStyles = makeStyles({
  ...dashboardStyle,
  formControl: {
    ...dashboardStyle.formControl,
    margin: "8px 12px 8px 0",
    minWidth: "200px",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: "8px",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "24px",
  },
  hint: {
    marginTop: "4px",
    color: "rgba(0,0,0,0.54)",
    fontSize: "12px",
  },
});

const TABLE_HEAD = [
  "Username",
  "Email",
  "Days since last connection",
  "Earnings (period)",
  "Priority",
];

export default function InactiveSuccessfulCreators() {
  const classes = useStyles();
  const context = useContext(AppContext);

  const [lookbackMonthsInput, setLookbackMonthsInput] = useState("3");
  const [minPeriodEarningsInput, setMinPeriodEarningsInput] = useState("1000");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    const lookbackMonths = parseInt(
      String(lookbackMonthsInput).trim(),
      10
    );
    const minPeriodEarnings = parseFloat(
      String(minPeriodEarningsInput).replace(",", ".").trim()
    );

    if (Number.isNaN(lookbackMonths) || lookbackMonths < 1) {
      setError(
        "Analysis period (months) must be an integer greater than or equal to 1."
      );
      return;
    }
    if (Number.isNaN(minPeriodEarnings) || minPeriodEarnings < 0) {
      setError("Minimum earnings threshold must be zero or a positive number.");
      return;
    }

    setError(null);
    setLoading(true);

    const authService = new AuthService();
    authService.setUrl(
      context.apiUrl + context.usersHonoPath + "inactiveSuccessfulCreators"
    );
    authService.request(
      "get",
      { lookbackMonths, minPeriodEarnings },
      (response) => {
        setLoading(false);
        const data = response.data;
        if (data && typeof data.error === "string") {
          setError(data.error);
          setRows([]);
          return;
        }
        if (!Array.isArray(data)) {
          setError("Unexpected server response.");
          setRows([]);
          return;
        }
        setRows(data);
      },
      () => {
        setLoading(false);
        setError("Could not reach the API (network or server error).");
        setRows([]);
      }
    );
  }, [
    context.apiUrl,
    context.usersHonoPath,
    lookbackMonthsInput,
    minPeriodEarningsInput,
  ]);

  useEffect(() => {
    fetchData();
    // Initial load only; filter changes are applied via Refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableData = rows.map((r) => [
    r.userName ?? "",
    r.mail ?? "",
    r.lastConnectionDays != null
      ? Number(r.lastConnectionDays).toLocaleString("en-US", {
          maximumFractionDigits: 2,
        })
      : "",
    r.earnings != null
      ? Number(r.earnings).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "",
    renderPriorityLabel(r.priority),
  ]);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="warning" stats icon>
              <CardIcon color="warning">
                <TrendingDown />
              </CardIcon>
              <p className={classes.cardCategory}>
                Inactive successful creators
              </p>
            </CardHeader>
            <CardBody>

              <Box className={classes.filterRow}>
                <TextField
                  className={classes.formControl}
                  label="Window (months)"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={lookbackMonthsInput}
                  onChange={(e) => setLookbackMonthsInput(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  className={classes.formControl}
                  label="Min. earnings threshold"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={minPeriodEarningsInput}
                  onChange={(e) => setMinPeriodEarningsInput(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={fetchData}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
              <Typography className={classes.hint} component="p">
                <strong>Window</strong>: number of months in the analysis period.{" "}
                <strong>Threshold</strong>: minimum total earnings over that
                period to qualify as &quot;successful&quot;.
              </Typography>

              {error && (
                <Box mt={2}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              )}

              {loading ? (
                <div className={classes.spinnerContainer}>
                  <CircularProgress />
                </div>
              ) : tableData.length > 0 ? (
                <Table
                  tableHeaderColor="primary"
                  tableHead={TABLE_HEAD}
                  tableData={tableData}
                />
              ) : (
                !error && (
                  <Box mt={2}>
                    <Alert severity="info">
                      No creators match these criteria.
                    </Alert>
                  </Box>
                )
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
